import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Mantiene al dia el contenido de un bloque cuando cambia la variante.
 *
 * Ritual no re-renderiza la seccion entera: dispara `productSelect` sobre ella con una
 * promesa que resuelve al HTML recien renderizado, y cada componente reemplaza su
 * propio pedazo. Esto hace lo mismo que assets/product-price.js, que es la convencion
 * del tema.
 *
 * Deliberadamente no recalcula nada en el cliente: las reglas (montos minimos de
 * cuotas, umbral de stock) viven en Liquid, y una segunda copia en JavaScript se iria
 * separando de la primera con el tiempo.
 *
 * Uso:
 *   <variant-aware data-block-id="{{ block.id }}" data-product-id="{{ product.id }}">
 *     ...contenido calculado en Liquid...
 *   </variant-aware>
 *
 * @extends {Component<{}>}
 */
class VariantAware extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#section = null;
  }

  /** @type {Element | null} */
  #section = null;

  /**
   * @param {CustomEvent & { promise: Promise<{ detail: { html?: Document, productId?: string } }> }} event
   */
  #handleProductSelect = (event) => {
    /* Las tarjetas de producto traen su propio precio y su propio stock; este bloque
       solo sigue al producto principal de la pagina. */
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) return;

        const fresh = detail.html.querySelector(`variant-aware[data-block-id="${this.dataset.blockId}"]`);
        /* Sin equivalente en el HTML nuevo, esta variante no corresponde mostrar nada. */
        this.innerHTML = fresh ? fresh.innerHTML : '';
      })
      .catch(() => {
        /* Si el re-render falla, queda el contenido anterior: preferible a borrarlo o a
           mostrar un dato inventado. */
      });
  };
}

if (!customElements.get('variant-aware')) {
  customElements.define('variant-aware', VariantAware);
}
